/* ================================================================
   IndexedDB — Gestion Formation (CEJ CONCEPT)
   v2 — Étape 3 : File d'attente de mutations hors-ligne
   ================================================================

   Stores :
     api_responses  — snapshot des réponses GET (lecture hors-ligne)
     meta           — métadonnées (date dernier snapshot, etc.)
     mutation_queue — mutations en attente de synchronisation

   Événements window personnalisés :
     'gf:mutation-queued'  — une nouvelle mutation vient d'être mise en file
     'gf:mutation-synced'  — une mutation a été synchronisée (Step 4)
================================================================ */

const DB_NAME    = 'gf-offline-db';
const DB_VERSION = 2;          // <-- version bumped (ajout mutation_queue)

// ─────────────────────────────────────────────────────────────────
// SNAPSHOT_ENDPOINTS — données mises en cache lors du snapshot
// ─────────────────────────────────────────────────────────────────
export const SNAPSHOT_ENDPOINTS = [
  { label: 'Tableau de bord',  path: '/dashboard',            params: '' },
  { label: 'Avancement',       path: '/dashboard/avancement', params: '' },
  { label: 'Clients',          path: '/clients',              params: 'limit=9999' },
  { label: 'Formations',       path: '/formations',           params: 'limit=9999' },
  { label: 'Sessions',         path: '/sessions',             params: 'limit=9999' },
  { label: 'Stagiaires',       path: '/stagiaires',           params: 'limit=9999' },
  { label: 'Formateurs',       path: '/formateurs',           params: 'limit=9999' },
  { label: 'Devis',            path: '/devis',                params: 'limit=9999' },
];

// ─────────────────────────────────────────────────────────────────
// Ouverture / migration de la base IndexedDB
// ─────────────────────────────────────────────────────────────────
let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = e => {
      const db = e.target.result;

      // ── v1 : stores de base ──────────────────────────────────
      if (!db.objectStoreNames.contains('api_responses')) {
        db.createObjectStore('api_responses', { keyPath: 'path' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }

      // ── v2 : file d'attente des mutations ────────────────────
      if (!db.objectStoreNames.contains('mutation_queue')) {
        const store = db.createObjectStore('mutation_queue', {
          keyPath      : 'id',
          autoIncrement: true,
        });
        // Index sur le statut pour filtrer facilement les mutations pending
        store.createIndex('idx_status', 'status', { unique: false });
      }
    };

    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = e => {
      _dbPromise = null;      // Permettre de réessayer
      reject(e.target.error);
    };

    // Si la DB est ouverte ailleurs et doit upgrader
    req.onblocked = () => {
      console.warn('[IDB] Mise à niveau bloquée — fermer les autres onglets.');
    };
  });
  return _dbPromise;
}

// ─────────────────────────────────────────────────────────────────
// SECTION 1 — Cache des réponses API (lecture hors-ligne)
// ─────────────────────────────────────────────────────────────────

export async function getOfflineEntry(path) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction('api_responses', 'readonly');
    const req = tx.objectStore('api_responses').get(path);
    req.onsuccess = e => resolve(e.target.result || null);
    req.onerror   = e => reject(e.target.error);
  });
}

async function putOfflineEntry(path, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction('api_responses', 'readwrite');
    const req = tx.objectStore('api_responses').put({ path, data, timestamp: Date.now() });
    req.onsuccess = () => resolve();
    req.onerror   = e => reject(e.target.error);
  });
}

// Fallback appelé par apiFetch quand le serveur est inaccessible
// endpoint peut contenir des query params (ex: '/clients?page=1&limit=20')
export async function getOfflineCache(endpoint) {
  try {
    const basePath = '/' + endpoint.replace(/^\//, '').split('?')[0];

    // 1. Correspondance exacte
    const exact = await getOfflineEntry(basePath);
    if (exact) return exact.data;

    // 2. Pour les vues détail : '/clients/CL001' → essayer '/clients'
    const segments = basePath.split('/').filter(Boolean);
    if (segments.length > 1) {
      const parent = '/' + segments[0];
      const parentEntry = await getOfflineEntry(parent);
      if (parentEntry) return parentEntry.data;
    }

    return null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// SECTION 2 — Métadonnées du snapshot
// ─────────────────────────────────────────────────────────────────

export async function getSnapshotMeta() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction('meta', 'readonly');
      const req = tx.objectStore('meta').get('snapshot');
      req.onsuccess = e => resolve(e.target.result?.value || null);
      req.onerror   = e => reject(e.target.error);
    });
  } catch {
    return null;
  }
}

async function putSnapshotMeta(value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction('meta', 'readwrite');
    const req = tx.objectStore('meta').put({ key: 'snapshot', value });
    req.onsuccess = () => resolve();
    req.onerror   = e => reject(e.target.error);
  });
}

// ─────────────────────────────────────────────────────────────────
// SECTION 3 — File d'attente des mutations hors-ligne
// ─────────────────────────────────────────────────────────────────

/**
 * Ajoute une mutation dans la file d'attente.
 *
 * @param {string} method   — 'POST' | 'PUT' | 'PATCH' | 'DELETE'
 * @param {string} endpoint — chemin API relatif (ex: '/presences/123')
 * @param {object|null} body — corps de la requête (déjà parsé en objet)
 * @param {string} offlineId — ID temporaire généré par apiFetch
 * @returns {Promise<number>} — ID de la mutation dans la queue
 */
export async function queueMutation(method, endpoint, body, offlineId) {
  const db = await openDB();
  const id = await new Promise((resolve, reject) => {
    const tx  = db.transaction('mutation_queue', 'readwrite');
    const req = tx.objectStore('mutation_queue').add({
      timestamp  : Date.now(),
      method,
      endpoint,
      body       : body || null,
      offline_id : offlineId,
      retries    : 0,
      status     : 'pending',     // 'pending' | 'conflict' | 'error'
      error_msg  : null,
    });
    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = e => reject(e.target.error);
  });

  // Notifier les composants qu'une mutation vient d'être mise en file
  window.dispatchEvent(new CustomEvent('gf:mutation-queued', { detail: { id, method, endpoint } }));
  return id;
}

/**
 * Retourne toutes les mutations avec statut 'pending' ou 'error',
 * triées par timestamp croissant (les plus anciennes d'abord).
 */
export async function getPendingMutations() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx      = db.transaction('mutation_queue', 'readonly');
    const store   = tx.objectStore('mutation_queue');
    const results = [];
    const req     = store.openCursor();
    req.onsuccess = e => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.status === 'pending' || cursor.value.status === 'error') {
          results.push(cursor.value);
        }
        cursor.continue();
      } else {
        results.sort((a, b) => a.timestamp - b.timestamp);
        resolve(results);
      }
    };
    req.onerror = e => reject(e.target.error);
  });
}

/**
 * Retourne le nombre de mutations en attente (pending ou error).
 */
export async function getMutationCount() {
  try {
    const list = await getPendingMutations();
    return list.length;
  } catch {
    return 0;
  }
}

/**
 * Supprime une mutation de la file (après synchronisation réussie).
 * Dispatche 'gf:mutation-synced' pour mettre à jour le badge.
 */
export async function removeMutation(id) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx  = db.transaction('mutation_queue', 'readwrite');
    const req = tx.objectStore('mutation_queue').delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = e => reject(e.target.error);
  });
  window.dispatchEvent(new CustomEvent('gf:mutation-synced', { detail: { id } }));
}

/**
 * Met à jour le statut d'une mutation (ex: 'error' + message).
 * Dispatche 'gf:mutation-synced' pour rafraîchir le badge.
 */
export async function updateMutationStatus(id, status, errorMsg = null) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx    = db.transaction('mutation_queue', 'readwrite');
    const store = tx.objectStore('mutation_queue');
    const req   = store.get(id);
    req.onsuccess = e => {
      const record = e.target.result;
      if (!record) { resolve(); return; }
      record.status    = status;
      record.error_msg = errorMsg;
      record.retries   = (record.retries || 0) + 1;
      const putReq = store.put(record);
      putReq.onsuccess = () => resolve();
      putReq.onerror   = ev => reject(ev.target.error);
    };
    req.onerror = e => reject(e.target.error);
  });
  window.dispatchEvent(new CustomEvent('gf:mutation-synced', { detail: { id } }));
}

// ─────────────────────────────────────────────────────────────────
// SECTION 4 — Snapshot complet (inchangé depuis Step 2)
// ─────────────────────────────────────────────────────────────────

export async function runSnapshot(apiBase, token, onProgress) {
  const total  = SNAPSHOT_ENDPOINTS.length;
  let   done   = 0;
  let   errors = 0;

  for (const ep of SNAPSHOT_ENDPOINTS) {
    onProgress(done, total, ep.label);
    const sep = ep.params ? '?' : '';
    const url = `${apiBase}${ep.path}${sep}${ep.params}`;
    try {
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        cache: 'no-store',
      });
      if (res.ok) {
        const data = await res.json();
        await putOfflineEntry(ep.path, data);
      } else {
        errors++;
      }
    } catch (_) {
      errors++;
    }
    done++;
  }

  const meta = {
    timestamp: Date.now(),
    total,
    errors,
    status: errors === 0 ? 'ok' : errors < total ? 'partial' : 'error',
  };
  await putSnapshotMeta(meta);
  onProgress(total, total, 'Terminé');
  return meta;
}
